/**
 * This service provides captured tracking params to any components that
 * need them. E.g., the phone number changes as a function of the site name,
 * and the contact form submits these captured parameters to nestio.
 *
 * Right now, we get all these parameters simply by parsing the query string
 * on initial page load (see the main index.js, which invokes this service).
 * In the future, though, we might store the parmeters in a cookie or local
 * storage for more robustness.
 */
import { parseSearchString } from "../utility/utility";
import { logOrThrowError } from "../utility/logging";

const UTM_NAMES = [
  'SiteName',
  'CampaignName',
  'PlacementName',
  'PlacementID',
  'AdName',
  'AdID'
];

/*
 * Why set utms and query by calling the captureQueryParams function, rather
 * than just initializing them in this outer scope? If we did the latter, it's
 * true that we would guarantee that those variables are always defined before a
 * getTrackingParam function is called, which we want. The problem is: if none
 * of the components initially rendered on the page depend on a tracking param,
 * then this module won't be imported until after the user has changed routes.
 * At that point, though, the tracking qurey parameters will be gone(!!), because
 * the router doesn't pass them between routes. So we actually have to make sure
 * that the query params are captured on initial page load, rather than merely
 * before a tracking params getter function is called. And there are two ways to
 * do that: 1) import this module in index.js/GlobalLayoutContainer (an app
 * entry point) and that import would, as a side effect, set these outer-scoped
 * variables. However, that's very unclear, and it'd be easy to remove the import
 * thinking that it's unused--some aggressive linters might even do that for us.
 * So, we create this function--captureQueryParams--that we can call explicitly
 * on app start, in GlobalLayoutContainer.
 */
let utms, query;
const hasOwnProp = Object.prototype.hasOwnProperty;
export function captureQueryParams(queryString) {
  query = parseSearchString(queryString);
  utms = makeUtmsObject(query, UTM_NAMES, undefined);
}

export function getAllTrackingParams(queryDefaultVal = undefined) {
  assertServiceHasBeenSetup();

  return {
    pathname: utms,
    query: (queryDefaultVal === undefined) 
      ? {...query} 
      : Object.keys(query).reduce((queryWithDefaults, paramName) => {
          queryWithDefaults[paramName] = hasOwnProp.call(query, paramName) ? query[paramName] : queryDefaultVal;
          return queryWithDefaults;
        }, Object.create(null))
  };
}

export function getTrackingParam(paramName, defaultVal = undefined) {
  assertServiceHasBeenSetup();
  return utms[paramName] !== undefined ? utms[paramName] : defaultVal;
}

function makeUtmsObject(query, utmNames, defaultVal) {
  assertServiceHasBeenSetup(true);

  return utmNames.reduce((utms, utm) => ({
    ...utms,
    [utm]: query[utm] !== undefined ? query[utm] : defaultVal
  }), Object.create(null));
}

// In dev only, throw if the user tries to access tracking
// params before calling captureQueryParams()
function assertServiceHasBeenSetup(checkQueryOnly = false) {
  let varsAreUninitialized =
    query === undefined && (checkQueryOnly ? true : utms === undefined);

  if(varsAreUninitialized) {
    logOrThrowError(
      new Error("You must call captureQueryParms() before getting a tracking param.")
    );
  }
}



// WEBPACK FOOTER //
// ./client/services/trackingParams.js